import { createSiteApi } from '@/api-calls/sites/mutate'
import { createSiteSchema } from '@/lib/schema/sites'
import { getApiUrl } from '@/utils/api-utils'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@blogging/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, withQueryClientProvider } from '@ngtrvu/use-api'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

function OnboardingNewPageContent() {
  const navigate = useNavigate()
  const [slugStatus, setSlugStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken'
  >('idle')

  const form = useForm({
    resolver: zodResolver(createSiteSchema),
    defaultValues: {
      name: '',
      title: '',
      slug: '',
    },
  })

  const { mutate: createSite, isPending: isCreating } = useMutation(
    createSiteApi,
    {
      resourceName: 'site',
      onSuccess(data: any) {
        const { site } = data
        navigate(`/${site.slug || site.id}`)
      },
      onError(error: any) {
        toast.error(error.message || 'Failed to create site')
      },
    },
  )

  const generateSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-') // Replace non-alphanumeric chars with dashes
      .replace(/-+/g, '-') // Replace multiple dashes with single dash
      .replace(/^-|-$/g, '') // Remove leading/trailing dashes
  }

  const checkSlugAvailability = useCallback(
    async (slug: string) => {
      if (!slug || slug.length < 3) {
        setSlugStatus('idle')
        return
      }

      setSlugStatus('checking')
      try {
        const response = await fetch(
          getApiUrl(`/api/public/v1/sites/slug/${slug}`),
        )
        if (response.status === 404) {
          // Slug not found means it's available
          setSlugStatus('available')
        } else {
          // Slug exists
          setSlugStatus('taken')
          form.setError('slug', {
            type: 'manual',
            message: 'This subdomain is already taken',
          })
        }
      } catch (error) {
        setSlugStatus('idle')
      }
    },
    [form],
  )

  const updateSlugFromName = () => {
    const name = form.getValues('name')
    const currentSlug = form.getValues('slug')
    // Only auto-generate if slug is empty
    if (name && !currentSlug) {
      const slug = generateSlug(name)
      form.setValue('slug', slug, { shouldValidate: true })
      checkSlugAvailability(slug)
    }
  }

  const onSubmit = async (data: any) => {
    try {
      createSite(data)
    } catch (error) {
      console.error('Failed to create site:', error)
    }
  }

  return (
    <div className="container relative min-h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      {/* Left side - Brand panel */}
      <div className="relative hidden h-full flex-col bg-zinc-900 p-10 text-white lg:flex dark:border-r dark:border-zinc-800">
        <div className="absolute inset-0 bg-zinc-900" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          {/* <Command className="mr-2 h-6 w-6" /> */}
          blogging.dev
        </div>
        <div className="relative z-20 mt-auto">
          <blockquote className="space-y-2">
            <p className="text-lg">
              &ldquo;Start your blogging journey today. Create a beautiful, fast
              blog in minutes with blogging.dev.&rdquo;
            </p>
            <footer className="text-sm text-zinc-400">
              Your content, your way
            </footer>
          </blockquote>
        </div>
      </div>

      {/* Right side - Create site form */}
      <div className="flex min-h-screen flex-col items-center justify-center p-4 lg:min-h-0 lg:p-8">
        {/* Mobile header */}
        <div className="mb-8 text-center lg:hidden">
          <h2 className="text-xl font-semibold">blogging.dev</h2>
        </div>

        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[400px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Create your site
            </h1>
            <p className="text-sm text-muted-foreground">
              Set up your new blog in just a few steps
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Site Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="My Blog"
                        {...field}
                        onBlur={() => {
                          field.onBlur()
                          updateSlugFromName()
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Site Title</FormLabel>
                    <FormControl>
                      <Input placeholder="My Awesome Blog" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subdomain</FormLabel>
                    <FormControl>
                      <div className="flex items-center">
                        <div className="relative flex-1">
                          <Input
                            placeholder="my-blog"
                            className="rounded-r-none pr-8"
                            {...field}
                            onChange={(e) => {
                              const value = generateSlug(e.target.value)
                              field.onChange(value)
                              setSlugStatus('idle')
                              form.clearErrors('slug')
                            }}
                            onBlur={() => {
                              field.onBlur()
                              checkSlugAvailability(field.value)
                            }}
                          />
                          {slugStatus !== 'idle' && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              {slugStatus === 'checking' && (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              )}
                              {slugStatus === 'available' && (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              )}
                              {slugStatus === 'taken' && (
                                <XCircle className="h-4 w-4 text-destructive" />
                              )}
                            </div>
                          )}
                        </div>
                        <span className="inline-flex items-center px-3 h-10 border border-l-0 border-input bg-muted text-muted-foreground text-sm rounded-r-md whitespace-nowrap">
                          .blgg.dev
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="rounded-lg border bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">
                  You can add a custom domain and customize your site's
                  appearance after creation.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={
                  isCreating ||
                  slugStatus === 'taken' ||
                  slugStatus === 'checking'
                }
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating your site...
                  </>
                ) : (
                  'Create Site'
                )}
              </Button>
            </form>
          </Form>

          <p className="px-8 text-center text-sm text-muted-foreground">
            By creating a site, you agree to our{' '}
            <a
              href="https://blogging.dev/terms"
              className="underline underline-offset-4 hover:text-primary"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://blogging.dev/privacy"
              className="underline underline-offset-4 hover:text-primary"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}

export const OnboardingNewPage = withQueryClientProvider(
  OnboardingNewPageContent,
)
